import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    FadeIn,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Freeze } from '@/components/Freeze';
import { LeftSideTabBar, TABS_HEIGHT } from '@/components/LeftSideTabBar';
import { SizedBox } from '@/components/SizedBox';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { SHEET_BORDER_RADIUS } from '@/feature/circular-pager/CircularPageSlot';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { PICK_BAR_HEIGHT } from '@/feature/lobby/components/FantasyPickbar';
import { StickyCellRendererComponent } from '@/feature/lobby/components/sticky-item/StickyCellRendererComponent';
import { StickyWrapper, useStickyScrollHandler } from '@/feature/lobby/hooks/use-sticky-translation';
import { PlayerProfileEventSection } from '@/feature/player-profile-sheets/components/PlayerProfileEventSection';
import { PlayerProfileHeader } from '@/feature/player-profile-sheets/components/PlayerProfileHeader';
import { PlayerProfileInsights } from '@/feature/player-profile-sheets/components/PlayerProfileInsights';
import { useSocialFeedEnabled } from '@/feature/social-feed/hooks/use-social-feed-enabled';
import { common } from '@/styles/styles';
import { isAndroid, isIos, showScrollIndicator } from '@/utils/constants-platform-specific';
import { isWeb } from '@/utils/constants-platform-specific';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';

import { PLAYER_CAROUSEL_TAB_BAR_HEIGHT } from '../PlayerProfileSheets';
import { ProjectionsList } from '../ProjectionsList';
import { PlayerProfileLineups } from './PlayerProfileLineups';
import { PlayerProfileSheetHandle } from './PlayerProfileSheetHandle';

const ScrollContainer = isWeb ? ScrollView : BottomSheetScrollView;

export const PlayerProfileSheet = ({
    shouldFreeze,
    playerInfo,
    isActive,
}: {
    shouldFreeze: boolean;
    playerInfo: PlayerWithTeamAndEvent;
    isActive: boolean;
}) => {
    /**
     * ! Do not use <Freeze> here as it will cause the BottomSheetScrollView to not scroll properly
     */
    if (shouldFreeze) {
        return null;
    }

    return <PlayerProfileSheetScreen playerInfo={playerInfo} isActive={isActive} />;
};

const TAB_IDS = {
    projections: 'projections-tab',
    insights: 'insights-tab',
    lineups: 'lineups-tab',
} as const;

const TABS = [
    { id: TAB_IDS.projections, title: 'Projections' },
    { id: TAB_IDS.insights, title: 'Insights' },
    { id: TAB_IDS.lineups, title: 'Lineups' },
];

export const PlayerProfileSheetScreen = memo(
    ({
        playerInfo,
        closeSinglePlayerProfile,
        isActive,
    }: {
        playerInfo: PlayerWithTeamAndEvent;
        closeSinglePlayerProfile?: () => void;
        isActive: boolean;
    }) => {
        const socialFeedEnabled = useSocialFeedEnabled();
        const filteredTabs = useMemo(() => {
            if (!socialFeedEnabled) {
                return TABS.filter(tab => tab.id !== TAB_IDS.lineups);
            }
            return TABS;
        }, [socialFeedEnabled]);

        const tabsStickPosition = useSharedValue(0);
        const insets = useSafeAreaInsets();
        const { scrollOffset: modalScrollOffset, sheetScrollableHook } = useScrollableModalContext();
        const [activeTab, setActiveTab] = useState<string>(filteredTabs[0].id);
        const { scrollDirection, scrollOffset, scrollVelocity } = useStickyScrollHandler(modalScrollOffset);

        // on web, the player carousel is sticky so we need to add the padding for it
        const paddingBottom =
            insets.bottom + insets.top + (isWeb ? PLAYER_CAROUSEL_TAB_BAR_HEIGHT : 0) + PICK_BAR_HEIGHT;

        const [shouldRenderProjectionList, setShouldRenderProjectionList] = useState(false);
        const { sheetMountAnimationProgress, animatedIndex } = useFullScreenPagerTransition();

        const onTabsLayout = useCallback(
            (event: LayoutChangeEvent) => {
                tabsStickPosition.value = event.nativeEvent.layout.y - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;
                // scroll offset was being persisted through multiple openings of the single player sheet
                // reset it so when we open the sheet, the sticky translation
                // is calculated correctly from the start
                scrollOffset.value = 0;
            },
            [scrollOffset, tabsStickPosition]
        );

        useAnimatedReaction(
            () => sheetMountAnimationProgress.value,
            current => {
                if (current !== null) {
                    //we don't show the projection list when the screen transition/mount starts, for better performance
                    //we only start showing the projection list after 70% of the mount animation is completed
                    if (sheetMountAnimationProgress.value > -0.7 && !shouldRenderProjectionList) {
                        runOnJS(setShouldRenderProjectionList)(true);
                    }
                }
            }
        );

        const listRef = useRef<ScrollView | null>(null);

        const setTab = useCallback(
            (tab: string) => {
                setActiveTab(tab);

                // When the player profile header is collapsed, scroll to the stick position of the tabs
                // to avoid weird jumps when changing between projections and insights
                if (scrollOffset.value > tabsStickPosition.value) {
                    listRef.current?.scrollTo({ animated: false, x: 0, y: tabsStickPosition.get() });
                }
            },
            [scrollOffset, tabsStickPosition]
        );
        const previousIsActive = useRef(isActive);

        /**
         * Always snap the scroll view to the top whenever this bottom sheet is in-active.
         *
         * If we skip this step, BottomSheetScrollView sometimes loses its link to the bottom sheet.
         * When the sheet is brought back to the front the list won’t move,
         * and touch events get routed only to the pan-gesture handler—so the sheet
         * feels frozen/unscrollable
         *
         * Combined with removing the Freeze component, this workaround keeps the list
         * scrollable every time the sheet is re-activated.
         */
        useEffect(() => {
            if (!isActive && previousIsActive.current) {
                listRef.current?.scrollTo({ x: 0, animated: false });
            }
            previousIsActive.current = isActive;
        }, [isActive, listRef]);

        const { bottomSheetCircularPagerRef } = useFullScreenPagerTransition();

        useEffect(() => {
            if (isAndroid) {
                // On Android, the BottomSheet can get stuck in the "closed" state
                // To fix this, we snap to the intended index on mount.
                bottomSheetCircularPagerRef.current?.snapToIndex(animatedIndex.value);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const { height } = useWindowDimensions();

        const containerStyle = useAnimatedStyle(() => {
            return {
                paddingBottom: paddingBottom,
                minHeight: height + tabsStickPosition.value,
            };
        });

        // set a minHeight so the header doesn't jump when switching between tabs with different heights
        const tabContentMinHeightStyle = useAnimatedStyle(() => {
            return {
                minHeight: height - tabsStickPosition.value - insets.top - TABS_HEIGHT - paddingBottom,
            };
        });

        const tabContent = useMemo(() => {
            switch (activeTab) {
                case TAB_IDS.projections:
                    return (
                        <Freeze freeze={!shouldRenderProjectionList || !isActive} placeholder={null}>
                            {/* only enabled on ios because the animation was causing UI issues on android */}
                            <Animated.View
                                entering={isIos ? FadeIn.duration(100) : undefined}
                                style={common.zIndexNegative1}
                            >
                                <PlayerProfileEventSection
                                    playerInfo={playerInfo}
                                    closeSinglePlayerProfile={closeSinglePlayerProfile}
                                />
                                <Box px={'s16'} paddingTop={'s16'} height={'100%'} paddingBottom={'s40'}>
                                    <ProjectionsList playerWithTeamAndEvent={playerInfo} />
                                </Box>
                            </Animated.View>
                        </Freeze>
                    );
                case TAB_IDS.insights:
                    BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PLAYER_TAB_INSIGHTS, {
                        league: playerInfo.event.league,
                    });
                    return (
                        <Animated.View style={tabContentMinHeightStyle}>
                            <PlayerProfileInsights playerId={playerInfo.id} league={playerInfo.league} />
                        </Animated.View>
                    );
                case TAB_IDS.lineups:
                    BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_PLAYER_TAB_LINEUPS, {
                        league: playerInfo.event.league,
                    });
                    return (
                        <Animated.View style={tabContentMinHeightStyle}>
                            <PlayerProfileLineups playerId={playerInfo.id} />
                        </Animated.View>
                    );
                default:
                    return null;
            }
        }, [
            activeTab,
            closeSinglePlayerProfile,
            isActive,
            playerInfo,
            tabContentMinHeightStyle,
            shouldRenderProjectionList,
        ]);

        return (
            <Box
                flex={1}
                minHeight={'100%'}
                backgroundColor={ds.colorToken.background.neutral.base}
                style={styles.bottomSheetContainer}
                testID={'player-profile-sheet'}
            >
                <PlayerProfileSheetHandle playerInfo={playerInfo} />
                <ScrollContainer
                    showsVerticalScrollIndicator={showScrollIndicator}
                    scrollEventsHandlersHook={sheetScrollableHook}
                    style={styles.bottomSheetScrollContainer}
                    ref={listRef}
                >
                    <Animated.View style={containerStyle}>
                        <Box style={common.zIndex100}>
                            <PlayerProfileHeader scrollOffset={scrollOffset} playerInfo={playerInfo} />
                        </Box>
                        <StickyCellRendererComponent
                            shouldBeSticky={true}
                            onLayout={onTabsLayout}
                            style={common.zIndex100}
                        >
                            <StickyWrapper
                                scrollOffset={scrollOffset}
                                initialPosition={tabsStickPosition}
                                scrollDirection={scrollDirection}
                                scrollVelocity={scrollVelocity}
                            >
                                <LeftSideTabBar
                                    tabs={filteredTabs}
                                    location={'player-profile-sheet'}
                                    setSelectedTab={setTab}
                                    containerStyle={common.full}
                                />
                            </StickyWrapper>
                        </StickyCellRendererComponent>

                        <SizedBox value={24} />
                        {tabContent}
                    </Animated.View>
                </ScrollContainer>
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    bottomSheetContainer: {
        overflow: 'hidden',
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    bottomSheetScrollContainer: {
        minHeight: '100%',
    },
});
